import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import {
  getAmazonIntegrations,
  getDepopIntegrations,
  getEBayIntegrations,
  getEtsyIntegrations,
  getMercariIntegrations,
  getPoshmarkIntegrations,
  getVintedIntegrations,
} from "src/store/plaidIntegration/selector";
import { fetchNewEbayListingsApi, updateUserApi } from "src/apiService";
import { amazonDirectImportInventory } from "src/apiService/modules/amazon";
import { amazonIntegrationSyncChange } from "src/apiService/modules/amazon";
import { etsyFetchListings } from "src/apiService/modules/etsy";
import { etsyIntegrationSyncChange } from "src/apiService/modules/etsy";
import { eBayIntegrationSyncChange } from "src/apiService/modules/ebay";
import { poshmarkIntegrationSyncChange } from "src/apiService/modules/poshmark";
import { mercariIntegrationSyncChange } from "src/apiService/modules/mercari";
import { depopIntegrationSyncChange } from "src/apiService/modules/depop";
import { vintedIntegrationSyncChange } from "src/apiService/modules/vinted";
import { poshmarkSyncInventory } from "src/store/integrationSync/actions/poshmark";
import { mercariSyncInventory } from "src/store/integrationSync/actions/mercari";
import { depopSyncInventory } from "src/store/integrationSync/actions/depop";
import { vintedSyncInventory } from "src/store/integrationSync/actions/vinted";
import InventoryPurchaseInfoDialog from "src/pages/GetStarted/Onboarding/InventoryPurchaseInfoDialog";

interface InventoryTrackingModalProps {
  open: boolean;
  /**
   * `declinedInventoryImport` is true when the user had platforms to import
   * from and chose "Don't Import".
   */
  onClose: (declinedInventoryImport: boolean) => void;
}

type IntegrationType =
  | "Ebay"
  | "Amazon"
  | "Etsy"
  | "Poshmark"
  | "Mercari"
  | "Vinted"
  | "Depop";
interface IntegrationOption {
  id: string;
  type: IntegrationType;
  label: string;
}

export const InventoryTrackingModal: React.FC<InventoryTrackingModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showPurchaseInfo, setShowPurchaseInfo] = useState(false);
  const [trackInventory, setTrackInventory] = useState<"yes" | "no" | null>(
    null
  );
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    string | null
  >(null);
  const [declinedImport, setDeclinedImport] = useState(false);
  const etsyIntegrations = useSelector(getEtsyIntegrations);
  const ebayIntegrations = useSelector(getEBayIntegrations);
  const amazonIntegrations = useSelector(getAmazonIntegrations);
  const poshmarkIntegrations = useSelector(getPoshmarkIntegrations);
  const mercariIntegrations = useSelector(getMercariIntegrations);
  const vintedIntegrations = useSelector(getVintedIntegrations);
  const depopIntegrations = useSelector(getDepopIntegrations);

  const integrations: IntegrationOption[] = [];
  if (etsyIntegrations.length) {
    integrations.push({
      id: etsyIntegrations[0].id,
      type: "Etsy",
      label: "Etsy",
    });
  }

  if (ebayIntegrations.length) {
    if (ebayIntegrations.length > 1) {
      ebayIntegrations.forEach((integration) => {
        integrations.push({
          id: integration.id,
          type: "Ebay",
          label: `Ebay - ${integration.username || integration.id}`,
        });
      });
    } else {
      integrations.push({
        id: ebayIntegrations[0].id,
        type: "Ebay",
        label: "Ebay",
      });
    }
  }

  if (amazonIntegrations.length) {
    integrations.push({
      id: amazonIntegrations[0].id,
      type: "Amazon",
      label: "Amazon",
    });
  }

  if (poshmarkIntegrations.length) {
    integrations.push({
      id: poshmarkIntegrations[0].id,
      type: "Poshmark",
      label: "Poshmark",
    });
  }

  if (mercariIntegrations.length) {
    integrations.push({
      id: mercariIntegrations[0].id,
      type: "Mercari",
      label: "Mercari",
    });
  }

  if (vintedIntegrations.length) {
    integrations.push({
      id: vintedIntegrations[0].id,
      type: "Vinted",
      label: "Vinted",
    });
  }

  if (depopIntegrations.length) {
    integrations.push({
      id: depopIntegrations[0].id,
      type: "Depop",
      label: "Depop",
    });
  }

  const handleContinue = () => {
    if (trackInventory === "no") {
      updateUserApi({ inventoryType: "cash" });
      onClose(false);
      resetModal();
    } else if (trackInventory === "yes") {
      updateUserApi({ inventoryType: "accrual" });
      if (integrations.length > 0) {
        setShowIntegrations(true);
      } else {
        setShowPurchaseInfo(true);
      }
    }
  };

  const handleSubmit = () => {
    if (!selectedIntegrationId) return;

    const selectedIntegration = integrations.find(
      (integration) => integration.id === selectedIntegrationId
    );
    if (!selectedIntegration) return;

    if (selectedIntegration.type === "Ebay") {
      eBayIntegrationSyncChange(selectedIntegration.id, true, true);
      fetchNewEbayListingsApi(selectedIntegration.id);
    } else if (selectedIntegration.type === "Amazon") {
      amazonIntegrationSyncChange(selectedIntegration.id, true, true);
      amazonDirectImportInventory(selectedIntegration.id);
    } else if (selectedIntegration.type === "Etsy") {
      etsyIntegrationSyncChange(selectedIntegration.id, true, true);
      etsyFetchListings();
    } else if (selectedIntegration.type === "Poshmark") {
      poshmarkIntegrationSyncChange(selectedIntegration.id, true, true);
      dispatch(poshmarkSyncInventory(selectedIntegration.id));
    } else if (selectedIntegration.type === "Mercari") {
      mercariIntegrationSyncChange(selectedIntegration.id, true, true);
      dispatch(mercariSyncInventory(selectedIntegration.id));
    } else if (selectedIntegration.type === "Vinted") {
      vintedIntegrationSyncChange(selectedIntegration.id, true, true);
      dispatch(vintedSyncInventory(selectedIntegration.id));
    } else if (selectedIntegration.type === "Depop") {
      depopIntegrationSyncChange(selectedIntegration.id, true, true);
      dispatch(depopSyncInventory(selectedIntegration.id));
    }

    setShowPurchaseInfo(true);
  };

  const resetModal = () => {
    setShowIntegrations(false);
    setShowPurchaseInfo(false);
    setTrackInventory(null);
    setSelectedIntegrationId(null);
    setDeclinedImport(false);
  };

  const handleClose = (event: object, reason: string) => {
    if (reason !== "backdropClick") {
      onClose(false);
      resetModal();
    }
  };

  return (
    <>
      <Dialog
        open={open && !showPurchaseInfo}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {showIntegrations ? "Import Inventory" : "Inventory Tracking"}
        </DialogTitle>
        <DialogContent>
          {!showIntegrations ? (
            <>
              <Typography>
                Would you like to track inventory in My Reseller Genie?
              </Typography>
              <RadioGroup
                value={trackInventory || ""}
                onChange={(e) =>
                  setTrackInventory(e.target.value as "yes" | "no")
                }
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </>
          ) : (
            <>
              <Typography>
                Select a platform to import its inventory (we recommend
                selecting your primary platform)
              </Typography>
              <RadioGroup
                value={selectedIntegrationId || ""}
                onChange={(e) =>
                  setSelectedIntegrationId(e.target.value || null)
                }
              >
                {integrations && integrations.length > 0 ? (
                  integrations.map((integration) => (
                    <FormControlLabel
                      key={integration.id}
                      value={integration.id}
                      control={<Radio />}
                      label={integration.label}
                    />
                  ))
                ) : (
                  <Typography>No integrations available</Typography>
                )}
              </RadioGroup>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!showIntegrations ? (
            <Button
              onClick={handleContinue}
              disabled={!trackInventory}
              variant="contained"
              color="primary"
            >
              Continue
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setDeclinedImport(true);
                  setShowPurchaseInfo(true);
                }}
                variant="contained"
                color="secondary"
              >
                Don't Import
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!selectedIntegrationId}
                variant="contained"
                color="primary"
              >
                Import Inventory
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <InventoryPurchaseInfoDialog
        open={showPurchaseInfo}
        onAcknowledge={() => {
          onClose(declinedImport);
          resetModal();
        }}
      />
    </>
  );
};
